import React, { useState } from 'react';
import { ShoppingCart, AlertCircle, CheckCircle, Calendar, Clock, Package, Building, DollarSign } from 'lucide-react';
import ViewLayout from '../ui/ViewLayout';

const CartView = ({ 
  props, 
  cart, 
  setCart, 
  organizations, 
  currentUser, 
  setActiveView, 
  removeFromCart,
  onCheckoutProps,
  onSubmitReservation,
  onSaveAsListFromCart // Add this prop
}) => {
  const [submittingReservation, setSubmittingReservation] = useState(false);
  const [showSaveListModal, setShowSaveListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  
  const cartProps = props.filter(prop => cart.includes(prop.id));
  const groupedByWarehouse = cartProps.reduce((acc, prop) => {
    const warehouse = prop.owner;
    if (!acc[warehouse]) {
      acc[warehouse] = [];
    }
    acc[warehouse].push(prop);
    return acc;
  }, {});

  const handleClearCart = () => {
    setCart([]);
  };

  const handleSaveAsList = () => {
    if (cart.length === 0) {
      alert('Your cart is empty!');
      return;
    }
    setShowSaveListModal(true);
  };

  const handleCreateListFromCart = () => {
    if (!newListName.trim()) {
      alert('Please enter a list name');
      return;
    }

    // Use the callback function passed from parent
    if (onSaveAsListFromCart) {
      onSaveAsListFromCart(cart, newListName, newListDescription);
      setNewListName('');
      setNewListDescription('');
      setShowSaveListModal(false);
      setCart([]); // Clear cart after saving as list
    }
  };

  const handleSubmitReservation = async (warehouse, warehouseProps) => {
    const warehouseData = document.querySelector(`[data-warehouse="${warehouse}"]`);
    const expectedReturnDate = warehouseData.querySelector('[name="expectedReturn"]')?.value;
    const purpose = warehouseData.querySelector('[name="purpose"]')?.value;
    const pickupDay = warehouseData.querySelector('[name="pickupDay"]')?.value;
    const timeRange = warehouseData.querySelector('[name="timeRange"]')?.value;
    const notes = warehouseData.querySelector('[name="notes"]')?.value;
    
    if (!expectedReturnDate) {
      alert('Please select an expected return date');
      return;
    }

    setSubmittingReservation(true);
    
    try {
      const propIds = warehouseProps.map(prop => prop.id);
      
      // Use the reservation submission function from props
      await onSubmitReservation(propIds, expectedReturnDate, purpose, notes, {
        preferredPickupDay: pickupDay,
        preferredTimeRange: timeRange,
        targetWarehouse: warehouse
      });
      
      // Remove these props from cart after successful submission
      const remainingCart = cart.filter(id => !propIds.includes(id));
      setCart(remainingCart);
      
    } catch (error) {
      console.error('Reservation submission error:', error);
    } finally {
      setSubmittingReservation(false);
    }
  };

  const handleCheckoutAtWarehouse = (warehouse, warehouseProps) => {
    const propIds = warehouseProps.map(prop => prop.id);
    const expectedReturnDate = document.querySelector(`[data-warehouse="${warehouse}"] [name="expectedReturn"]`)?.value;
    const purpose = document.querySelector(`[data-warehouse="${warehouse}"] [name="purpose"]`)?.value;
    
    if (!expectedReturnDate) {
      alert('Please select an expected return date');
      return;
    }
    
    onCheckoutProps(propIds, expectedReturnDate, purpose || 'General Use');
  };

  if (cartProps.length === 0) {
    const emptyActions = [
      <button
        onClick={handleClearCart}
        className="border border-gray-300 text-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-50"
      >
        Clear Cart
      </button>,
      <button
        onClick={handleSaveAsList}
        className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
      >
        Save as List
      </button>
    ];

    return (
      <ViewLayout
        title="Reservation Cart"
        subtitle="Your cart is empty"
        actions={emptyActions}
      >
        <div className="text-center py-12 text-gray-500">
          <ShoppingCart className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">Your reservation cart is empty</p>
          <p className="text-sm">Add props to your cart while browsing to reserve them for pickup</p>
          <button
            onClick={() => setActiveView('search')}
            className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Browse Props
          </button>
        </div>
      </ViewLayout>
    );
  }

  // Cart stats and actions for populated cart
  const stats = [
    <ViewLayout.Stat 
      title="Total Items" 
      value={cartProps.length} 
      icon={Package} 
      color="blue" 
    />,
    <ViewLayout.Stat 
      title="Warehouses" 
      value={Object.keys(groupedByWarehouse).length} 
      icon={Building} 
      color="green" 
    />,
    <ViewLayout.Stat 
      title="Transfer Cost" 
      value={`${cartProps.reduce((sum, prop) => sum + (prop.transfer_cost || 0), 0)}`} 
      icon={DollarSign} 
      color="orange" 
    />
  ];

  const actions = [
    <button
      onClick={() => setActiveView('requests')}
      className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
    >
      My Requests
    </button>,
    <button
      onClick={handleClearCart}
      className="border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50"
    >
      Clear Cart
    </button>,
    <button
      onClick={handleSaveAsList}
      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
    >
      Save as List
    </button>
  ];

  return (
    <>
      <ViewLayout
      title="Reservation Cart"
      subtitle={`${cartProps.length} items ready for reservation`}
      stats={stats}
      actions={actions}
    >
      <div className="space-y-6">

        {/* Grouped by Warehouse */}
        {Object.entries(groupedByWarehouse).map(([warehouse, warehouseProps]) => {
          const warehouseOrg = organizations.find(org => org.name === warehouse);
          const isOwnWarehouse = currentUser.organization === warehouse;
          
          return (
            <div key={warehouse} className="bg-white rounded-lg shadow-md">
              <div className="p-4 border-b bg-gray-50">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-lg">{warehouse}</h3>
                    <p className="text-sm text-gray-600">{warehouseOrg?.location || 'Location TBD'}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold">{warehouseProps.length} items</div>
                    <div className="text-sm text-gray-600">
                      {isOwnWarehouse ? 'Your Organization' : 'External Warehouse'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                <div className="space-y-3">
                  {warehouseProps.map(prop => (
                    <div key={prop.id} className="flex gap-4 p-3 border rounded-lg">
                      <img
                        src={prop.images[0]}
                        alt={prop.name}
                        className="w-16 h-12 object-cover rounded"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{prop.name}</h4>
                            <p className="text-sm text-gray-600">{prop.category}</p>
                            {prop.transfer_cost > 0 && (
                              <p className="text-sm text-green-600">Transfer: ${prop.transfer_cost}</p>
                            )}
                          </div>
                          <button
                            onClick={() => removeFromCart(prop.id)}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reservation Form for this warehouse */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg" data-warehouse={warehouse}>
                  {isOwnWarehouse ? (
                    /* Same Organization - Direct Checkout */
                    <>
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-800">
                          <CheckCircle className="h-4 w-4" />
                          <span className="font-medium">Direct Checkout Available</span>
                        </div>
                        <p className="text-sm text-green-700 mt-1">
                          These props belong to your organization, so you can check them out immediately.
                        </p>
                      </div>
                      <h4 className="font-semibold mb-4">Checkout Props from {warehouse}</h4>
                    </>
                  ) : (
                    /* Different Organization - Reservation Request */
                    <>
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2 text-blue-800">
                          <Calendar className="h-4 w-4" />
                          <span className="font-medium">Reservation Request Required</span>
                        </div>
                        <p className="text-sm text-blue-700 mt-1">
                          These props belong to {warehouse}. Your request will be sent to their warehouse admin for approval.
                        </p>
                      </div>
                      <h4 className="font-semibold mb-4">Request Reservation from {warehouse}</h4>
                    </>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {!isOwnWarehouse && (
                      <>
                        <div>
                          <label className="block text-sm font-medium mb-1">Preferred Pickup Day</label>
                          <select name="pickupDay" className="w-full border border-gray-300 rounded px-3 py-2">
                            <option value="">Select a day</option>
                            <option value="monday">Monday</option>
                            <option value="tuesday">Tuesday</option>
                            <option value="wednesday">Wednesday</option>
                            <option value="thursday">Thursday</option>
                            <option value="friday">Friday</option>
                            <option value="saturday">Saturday</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Preferred Time Range</label>
                          <select name="timeRange" className="w-full border border-gray-300 rounded px-3 py-2">
                            <option value="">Select time range</option>
                            <option value="morning">Morning (9am-12pm)</option>
                            <option value="afternoon">Afternoon (12pm-5pm)</option>
                            <option value="evening">Evening (5pm-8pm)</option>
                          </select>
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-sm font-medium mb-1">Expected Return Date *</label>
                      <input
                        type="date"
                        name="expectedReturn"
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Purpose/Production</label>
                      <input
                        type="text"
                        name="purpose"
                        placeholder="e.g., Hamlet, Wedding Reception, Art Installation"
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                  </div>
                  
                  {!isOwnWarehouse && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium mb-1">Special Requests/Notes</label>
                      <textarea
                        name="notes"
                        placeholder="Any special handling instructions, alternative return arrangements, etc."
                        className="w-full border border-gray-300 rounded px-3 py-2 h-20"
                      />
                    </div>
                  )}
                  
                  {isOwnWarehouse ? (
                    <button
                      onClick={() => handleCheckoutAtWarehouse(warehouse, warehouseProps)}
                      className="w-full mt-4 bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 font-medium"
                    >
                      ✅ Checkout {warehouseProps.length} Props Now
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSubmitReservation(warehouse, warehouseProps)}
                      disabled={submittingReservation}
                      className="w-full mt-4 bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                    >
                      {submittingReservation ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          Submitting...
                        </>
                      ) : (
                        <>Submit Reservation Request for {warehouse}</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Overall Notes */}
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-yellow-800 mb-1">Reservation Process:</p>
              <ul className="text-yellow-700 space-y-1">
                <li>• <strong>Submit Request:</strong> Your reservation request will be sent to the warehouse admin</li>
                <li>• <strong>Admin Review:</strong> Warehouse admin will approve/reject and schedule pickup</li>
                <li>• <strong>Confirmation:</strong> You'll receive confirmation with pickup appointment details</li>
                <li>• <strong>Pickup:</strong> Visit warehouse at scheduled time for final checkout</li>
                <li>• <strong>Return:</strong> Items can be returned to same warehouse or arranged via service provider</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ViewLayout>

    {/* Save as List Modal */}
    {showSaveListModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <h3 className="font-semibold text-lg mb-4">Save Cart as List</h3>
          <p className="text-sm text-gray-600 mb-4">
            Save these {cart.length} props as a new list for future reference.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">List Name *</label>
              <input
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="e.g., Hamlet Production Props"
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={newListDescription}
                onChange={(e) => setNewListDescription(e.target.value)}
                placeholder="Brief description of this list..."
                className="w-full border border-gray-300 rounded px-3 py-2 h-20"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCreateListFromCart}
                className="flex-1 bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
              >
                Create List
              </button>
              <button
                onClick={() => setShowSaveListModal(false)}
                className="flex-1 border border-gray-300 text-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CartView;